/**
 * ICE configuration: public STUN by default, TURN by opt-in.
 *
 * The charter asks for STUN/TURN, and those are two different kinds of thing.
 * STUN is a free, stateless "what does my address look like from out there"
 * question that public servers answer, so it ships as a default and covers
 * every peer pair that is not behind a symmetric NAT. TURN *relays the whole
 * media stream* and therefore costs real bandwidth, which means nobody sane
 * hard-codes somebody else's — so this port ships the **configuration
 * surface** for one (a URL and a credential pair, read out of `localStorage`)
 * rather than a hosted server, and says so in the lobby.
 *
 * Credentials live in `localStorage` deliberately: they are per-browser, the
 * player pastes their own, and nothing in this repo ever transmits them
 * anywhere except to the TURN server the player named.
 */

export interface TurnConfig {
  readonly url: string;
  readonly username: string;
  readonly credential: string;
}

export interface IceConfig {
  readonly stunUrls: readonly string[];
  readonly turn: TurnConfig | null;
}

/** Google's public STUN cluster — the conventional default, and no account needed. */
export const DEFAULT_STUN_URLS: readonly string[] = [
  "stun:stun.l.google.com:19302",
  "stun:stun1.l.google.com:19302",
];

export const TURN_STORAGE_KEY = "red-opus:turn";

export const DEFAULT_ICE: IceConfig = { stunUrls: DEFAULT_STUN_URLS, turn: null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads a stored TURN entry. Anything malformed is treated as "not
 * configured" rather than throwing: a bad `localStorage` value must not be
 * able to stop the player from using STUN.
 */
export function parseTurnConfig(raw: string | null): TurnConfig | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const { url, username, credential } = parsed;
  if (typeof url !== "string" || url.length === 0) return null;
  if (!url.startsWith("turn:") && !url.startsWith("turns:")) return null;
  return {
    url,
    username: typeof username === "string" ? username : "",
    credential: typeof credential === "string" ? credential : "",
  };
}

export function serializeTurnConfig(turn: TurnConfig): string {
  return JSON.stringify(turn);
}

/** The `RTCConfiguration.iceServers` list this config produces. */
export function iceServersFor(config: IceConfig): readonly RTCIceServer[] {
  const servers: RTCIceServer[] = [{ urls: [...config.stunUrls] }];
  if (config.turn) {
    servers.push({ urls: config.turn.url, username: config.turn.username, credential: config.turn.credential });
  }
  return servers;
}

/** One line for the lobby, so a player can see whether a relay is in play before they blame the NAT. */
export function describeIce(config: IceConfig): string {
  const stun = `${config.stunUrls.length} STUN server${config.stunUrls.length === 1 ? "" : "s"}`;
  return config.turn ? `${stun} + TURN relay (${config.turn.url})` : `${stun}, no TURN relay configured`;
}
